import { useCallback, useMemo, useState, type FC } from "react";
import {
  EditOutlined,
  UndoOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { Tooltip, message } from "antd";

import { Button, Modal } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { OperatorAffiliateDetail } from "../../../../types/affiliates";
import {
  useDeactivateAffiliate,
  useReactivateAffiliate,
} from "../../../../queries/affiliates/useDeactivateAffiliate";
import { useAffiliateContractDownload } from "../../../../queries/affiliates/useAffiliateReportDownloads";

import {
  formatAffiliateAddress,
  getAffiliateInitials,
} from "./affiliatePersonalInfoData";
import { personalInfoFromDetail } from "./operatorDetailAdapter";
import PaymentPlanCard from "./PaymentPlanCard/PaymentPlanCard";
import EditAffiliateInfoModal from "./EditAffiliateInfoModal";
import EditAffiliatePlanModal from "./EditAffiliatePlanModal";
import AffiliateContactActions from "./AffiliateContactActions";

import styles from "./AffiliateInfo.module.css";

export type AffiliateInfoProps = {
  affiliateId?: string;
  detail?: OperatorAffiliateDetail | null;
};

type LifecycleKind = "deactivate" | "reactivate";

const AffiliateInfo: FC<AffiliateInfoProps> = ({ affiliateId, detail }) => {
  const personalInfo = useMemo(
    () => (detail ? personalInfoFromDetail(detail) : null),
    [detail],
  );

  const fullName = personalInfo?.fullName ?? "";
  const initials = useMemo(() => getAffiliateInitials(fullName), [fullName]);
  const status = detail?.status ?? "active";
  const isActive = status === "active";
  const isDeactivated = status === "deactivated";

  const addressLines = useMemo(
    () => (personalInfo ? formatAffiliateAddress(personalInfo.address) : []),
    [personalInfo],
  );

  const [isEditInfoOpen, setEditInfoOpen] = useState(false);
  const [isEditPlanOpen, setEditPlanOpen] = useState(false);

  const [pendingLifecycle, setPendingLifecycle] =
    useState<LifecycleKind | null>(null);

  const deactivate = useDeactivateAffiliate();
  const reactivate = useReactivateAffiliate();
  const contractDownload = useAffiliateContractDownload();

  const hasContract = Boolean(detail?.contract_s3_key);
  const handleDownloadContract = useCallback(() => {
    const id = detail?.affiliate_id ?? affiliateId;
    if (!id) return;
    contractDownload.mutate({ affiliateId: id });
  }, [detail, affiliateId, contractDownload]);

  const runLifecycle = useCallback(
    async (kind: LifecycleKind) => {
      if (!detail) return;
      try {
        if (kind === "deactivate") {
          await deactivate.mutateAsync(detail.affiliate_id);
          message.success(AppStrings.DEACTIVATED_SUCCESS);
        } else {
          await reactivate.mutateAsync(detail.affiliate_id);
          message.success(AppStrings.REACTIVATED_SUCCESS);
        }
        setPendingLifecycle(null);
      } catch (err) {
        const envelope = (err as { response?: { data?: { message?: string } } })
          ?.response?.data;
        message.error(envelope?.message || AppStrings.LIFECYCLE_FAILED);
      }
    },
    [detail, deactivate, reactivate],
  );

  const handleEditInfo = () => {
    if (!detail || !isDeactivated) return;
    setEditInfoOpen(true);
  };

  const handleEditPlan = () => {
    if (!detail || !isActive) return;
    setEditPlanOpen(true);
  };

  const handleAddNote = () => {
    // TODO: Integrate endpoint when ready
  };

  const editInfoDisabled = !detail || !isDeactivated;
  const editPlanDisabled = !detail || !isActive;
  const lifecycleDisabled =
    !detail || deactivate.isPending || reactivate.isPending;

  const confirmTitle =
    pendingLifecycle === "deactivate"
      ? AppStrings.DEACTIVATE_AFFILIATE_TITLE
      : pendingLifecycle === "reactivate"
        ? AppStrings.REACTIVATE_AFFILIATE_TITLE
        : "";

  const confirmBody =
    pendingLifecycle === "deactivate"
      ? AppStrings.DEACTIVATE_CONFIRM_BODY
      : pendingLifecycle === "reactivate"
        ? AppStrings.REACTIVATE_CONFIRM_BODY
        : "";

  const confirmPrimaryLabel =
    pendingLifecycle === "deactivate"
      ? AppStrings.DEACTIVATE
      : AppStrings.REACTIVATE;

  return (
    <section
      className={styles.container}
      data-testid="affiliate-info"
      data-affiliate-id={affiliateId ?? ""}
    >
      <aside className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <span
            className={`${styles.statusBadge} ${
              isActive ? styles.statusActive : styles.statusDeactivated
            }`}
          >
            {isActive ? AppStrings.ACTIVE : AppStrings.DEACTIVATED_LABEL}
          </span>
        </div>

        <div className={styles.summaryAvatar} aria-hidden="true">
          {initials}
        </div>
        <h3 className={styles.summaryName}>{fullName || "—"}</h3>

        <div className={styles.paymentBlock}>
          <div className={styles.paymentHeader}>
            <span className={styles.paymentLabel}>
              {AppStrings.AFFILIATE_PAYMENT}
            </span>
            <Tooltip
              title={
                editPlanDisabled
                  ? AppStrings.PLAN_REQUIRES_ACTIVE
                  : undefined
              }
            >
              <Button
                intent="neutral"
                variant="ghost"
                iconLeft={<EditOutlined />}
                aria-label={`${AppStrings.EDIT} ${AppStrings.AFFILIATE_PAYMENT}`}
                onClick={handleEditPlan}
                disabled={editPlanDisabled}
                data-testid="affiliate-edit-plan-button"
              />
            </Tooltip>
          </div>
          {personalInfo?.payment ? (
            <PaymentPlanCard plan={personalInfo.payment} />
          ) : (
            <span
              className={styles.summaryName}
              data-testid="affiliate-no-payment-placeholder"
            >
              —
            </span>
          )}
        </div>

        <Button
          intent="neutral"
          variant="outline"
          onClick={handleAddNote}
          disabled
          className={styles.secondaryButton}
        >
          {AppStrings.ADD_NOTE}
        </Button>

        {isActive ? (
          <Button
            intent="danger"
            iconLeft={<UserDeleteOutlined />}
            onClick={() => setPendingLifecycle("deactivate")}
            disabled={lifecycleDisabled}
            data-testid="affiliate-deactivate-button"
            className={styles.deactivateButton}
          >
            {AppStrings.DEACTIVATE}
          </Button>
        ) : (
          <Button
            intent="primary"
            variant="outline"
            iconLeft={<UndoOutlined />}
            onClick={() => setPendingLifecycle("reactivate")}
            disabled={lifecycleDisabled}
            data-testid="affiliate-reactivate-button"
            className={styles.secondaryButton}
          >
            {AppStrings.REACTIVATE}
          </Button>
        )}
      </aside>

      <div className={styles.detailsColumn}>
        <article className={styles.detailsCard}>
          <header className={styles.detailsHeader}>
            <h3 className={styles.detailsTitle}>
              {AppStrings.PERSONAL_INFO}
            </h3>
            <Tooltip
              title={
                editInfoDisabled
                  ? AppStrings.INFO_REQUIRES_DEACTIVATED
                  : undefined
              }
            >
              <span>
                <Button
                  intent="neutral"
                  variant="ghost"
                  iconLeft={<EditOutlined />}
                  aria-label={`${AppStrings.EDIT} ${AppStrings.PERSONAL_INFO}`}
                  onClick={handleEditInfo}
                  disabled={editInfoDisabled}
                  data-testid="affiliate-edit-info-button"
                />
              </span>
            </Tooltip>
          </header>

          <dl className={styles.detailsList}>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>{AppStrings.NAME}:</dt>
              <dd className={styles.detailsValue}>
                <span className={styles.inlineAvatar} aria-hidden="true">
                  {initials}
                </span>
                <span>{fullName || "—"}</span>
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_CONTACT}:
              </dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.email ?? "—"}
                <AffiliateContactActions
                  affiliateId={detail ? detail.affiliate_id : undefined}
                  currentEmail={personalInfo?.email}
                />
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_DATE_JOINED}:
              </dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.joinedAt ?? "—"}
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>{AppStrings.USER_ID}:</dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.userId ?? "—"}
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_WEBSITE_URL}
              </dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.websiteUrl ?? "—"}
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>{AppStrings.ADDRESS}</dt>
              <dd
                className={`${styles.detailsValue} ${styles.detailsValueMultiline}`}
              >
                {addressLines.length
                  ? addressLines.map((line) => <span key={line}>{line}</span>)
                  : "—"}
              </dd>
            </div>
          </dl>
        </article>

        <article className={styles.detailsCard}>
          <header className={styles.detailsHeader}>
            <h3 className={styles.detailsTitle}>
              {AppStrings.AFFILIATE_CONTRACT}
            </h3>
          </header>
          <dl className={styles.detailsList}>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_CONTRACT_SIGNED}
              </dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.contract.signedAt ?? "—"}
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_CONTRACT_RENEWAL_DATE}
              </dt>
              <dd className={styles.detailsValue}>
                {personalInfo?.contract.renewalAt ?? "—"}
              </dd>
            </div>
            <div className={styles.detailsRow}>
              <dt className={styles.detailsLabel}>
                {AppStrings.AFFILIATE_CONTRACT_FILE}:
              </dt>
              <dd className={styles.detailsValue}>
                {hasContract ? (
                  <Button
                    intent="primary"
                    variant="ghost"
                    size="small"
                    onClick={handleDownloadContract}
                    loading={contractDownload.isPending}
                  >
                    {AppStrings.DOWNLOAD}
                  </Button>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>
        </article>
      </div>

      {detail && (
        <>
          <EditAffiliateInfoModal
            open={isEditInfoOpen}
            affiliate={detail}
            onClose={() => setEditInfoOpen(false)}
          />
          <EditAffiliatePlanModal
            open={isEditPlanOpen}
            affiliate={detail}
            onClose={() => setEditPlanOpen(false)}
          />
        </>
      )}

      <Modal
        open={pendingLifecycle !== null}
        onClose={() => setPendingLifecycle(null)}
        title={confirmTitle}
        size="md"
        intent={pendingLifecycle === "deactivate" ? "destructive" : "default"}
        data-testid="affiliate-lifecycle-confirm-modal"
        primaryAction={{
          label: confirmPrimaryLabel,
          onClick: () => {
            if (pendingLifecycle) runLifecycle(pendingLifecycle);
          },
          loading: deactivate.isPending || reactivate.isPending,
          disabled: deactivate.isPending || reactivate.isPending,
        }}
        secondaryAction={{
          label: AppStrings.CANCEL,
          onClick: () => setPendingLifecycle(null),
        }}
      >
        {/* Body copy lives here so the dialog header stays compact. */}
        <p className={styles.confirmBody}>{confirmBody}</p>
      </Modal>
    </section>
  );
};

export default AffiliateInfo;
